import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import type { RuleGroupType } from "react-querybuilder";
import {
  pageSectionSubtitleSx,
  pageSectionTitleSx,
} from "../../../constants/pageSectionStyles";
import arrowLeftIcon from "../../../assets/icon-left.svg";
import { brandContainedButtonSx } from "../../../constants/brandContainedButtonStyles";
import { useBrandThemeColors } from "../../../branding";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useAssessmentPlanFilterOptions } from "../../../hooks/useAssessmentPlanFilterOptions";
import { useConfigurableConcepts } from "../../../hooks/useConfigurableConcepts";
import { useHasPrivilegeStatus } from "../../../hooks/useHasPrivilege";
import { useAcTenantId } from "../../../hooks/useAcTenantId";
import {
  createConditionalPlan,
  resolvePlanSubTypeDisplayName,
  resolvePlanTypeDisplayName,
  updateConditionalPlan,
} from "../../../services/autoTaggingService";
import { AUTO_TAGGING_PRIVILEGES } from "../../../services/permissionsService";
import { useRegulationsNextShellIntegration } from "../../shared/useRegulationsNextShellIntegration";
import { buildAiPlanPath } from "../constants";
import {
  buildConditionalPlanCreatePayload,
  mapConditionPayloadToEditorQuery,
} from "./buildConditionalPlanPayload";
import DuplicateEnhancedTaggingRuleAlert from "./DuplicateEnhancedTaggingRuleAlert";
import EnhancedTaggingRuleConditionBuilder, {
  createEmptyConditionQuery,
} from "./EnhancedTaggingRuleConditionBuilder";
import DiscardEnhancedTaggingRuleChangesDialog from "./DiscardEnhancedTaggingRuleChangesDialog";
import ChangeObjectScopeDialog from "./ChangeObjectScopeDialog";
import EnhancedTaggingRuleFlowSidebar from "./EnhancedTaggingRuleFlowSidebar";
import EnhancedTaggingRuleObjectStep, {
  createEmptyObjectSelection,
  isObjectSelectionComplete,
  type EnhancedTaggingRuleObjectSelection,
} from "./EnhancedTaggingRuleObjectStep";
import EnhancedTaggingRuleThenStep, {
  createEmptyThenTag,
  isThenTagComplete,
  type EnhancedTaggingThenTag,
} from "./EnhancedTaggingRuleThenStep";
import { findDuplicateConditionalPlan, parseDuplicateConditionalPlanError } from "./findDuplicateConditionalPlan";
import { formatConditionSidebarSummary } from "./formatConditionNaturalLanguage";
import type {
  EnhancedTaggingRuleRow,
  EnhancedTaggingRuleWizardStep,
} from "./types";
import {
  hasEnhancedTaggingConditionData,
  validateEnhancedTaggingConditionQuery,
} from "./validateEnhancedTaggingCondition";

/** When → object criteria → conditions → then tag. */
const STEP_ORDER: EnhancedTaggingRuleWizardStep[] = [
  "when",
  "object",
  "if",
  "then",
];

const STEP_PANEL_META: Record<
  EnhancedTaggingRuleWizardStep,
  {
    title: string;
    iconBg: string;
    iconColor: string;
    Icon: typeof AccessTimeOutlinedIcon;
  }
> = {
  when: {
    title: "When: Auto-tagging runs",
    iconBg: "#E8F8EF",
    iconColor: "#22C55E",
    Icon: AccessTimeOutlinedIcon,
  },
  object: {
    title: "If: Object meets the criteria",
    iconBg: "#FFF7E6",
    iconColor: "#FAAD14",
    Icon: TuneOutlinedIcon,
  },
  if: {
    title: "If: Meet the condition(s)",
    iconBg: "#FFF7E6",
    iconColor: "#FAAD14",
    Icon: FilterAltOutlinedIcon,
  },
  then: {
    title: "Then: Apply tag",
    iconBg: "#EEF3FF",
    iconColor: "#295EF1",
    Icon: BoltOutlinedIcon,
  },
};

function mapRowToThenTag(row: EnhancedTaggingRuleRow): EnhancedTaggingThenTag {
  return {
    dimensionCode: row.conceptCode,
    dimensionLabel: row.dimension,
    valueCode: row.conceptValueCode,
    valueLabel: row.conceptValueName.trim() || row.value.trim(),
  };
}

export type EditEnhancedTaggingRuleLocationState = {
  rule: EnhancedTaggingRuleRow;
};

function readEditRuleFromLocation(
  ruleId: string,
  state: unknown,
): EnhancedTaggingRuleRow | null {
  if (!ruleId) return null;
  const rule = (state as EditEnhancedTaggingRuleLocationState | null)?.rule;
  if (!rule || rule.id !== ruleId) return null;
  return rule;
}

export default function AddEnhancedTaggingRulePage() {
  const acTenantId = useAcTenantId();
  const { ruleId = "" } = useParams<{ ruleId?: string }>();
  return (
    <AddEnhancedTaggingRulePageContent
      key={`${acTenantId || "no-ac-tenant"}:${ruleId || "new"}`}
      ruleId={ruleId.trim()}
    />
  );
}

function AddEnhancedTaggingRulePageContent({
  ruleId,
}: Readonly<{ ruleId: string }>) {
  useRegulationsNextShellIntegration();
  const navigate = useNavigate();
  const location = useLocation();
  const { primaryColor } = useBrandThemeColors();
  const { showMessage } = useSnackbar();
  const { planTypes, getObjectSubTypeOptions } =
    useAssessmentPlanFilterOptions();
  const { has: canManageAutoTagging, loaded: privilegesLoaded } =
    useHasPrivilegeStatus(AUTO_TAGGING_PRIVILEGES.manage);
  const isEditMode = Boolean(ruleId);
  const editRule = readEditRuleFromLocation(ruleId, location.state);
  const [activeStep, setActiveStep] =
    useState<EnhancedTaggingRuleWizardStep>("object");
  const [objectSelection, setObjectSelection] =
    useState<EnhancedTaggingRuleObjectSelection>(() =>
      editRule
        ? {
            objectType: editRule.objectType,
            objectSubType: editRule.objectSubType,
          }
        : createEmptyObjectSelection(),
    );
  const [showObjectValidation, setShowObjectValidation] = useState(false);
  const [conditionQuery, setConditionQuery] = useState<RuleGroupType>(() =>
    editRule
      ? mapConditionPayloadToEditorQuery(editRule.condition)
      : createEmptyConditionQuery(),
  );
  const [showConditionValidation, setShowConditionValidation] = useState(false);
  const [thenTag, setThenTag] = useState<EnhancedTaggingThenTag>(() =>
    editRule ? mapRowToThenTag(editRule) : createEmptyThenTag(),
  );
  const [showThenValidation, setShowThenValidation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [changeScopeConfirmOpen, setChangeScopeConfirmOpen] = useState(false);
  const [pendingObjectSelection, setPendingObjectSelection] =
    useState<EnhancedTaggingRuleObjectSelection | null>(null);
  /** Client-side list match (may be null when list lags behind backend). */
  const [clientDuplicateRuleId, setClientDuplicateRuleId] = useState<
    string | null
  >(null);
  /** Server-confirmed duplicate from create/update failure — not cleared by client poll. */
  const [submitDuplicateRuleId, setSubmitDuplicateRuleId] = useState<
    string | null
  >(null);
  const duplicateRuleId = submitDuplicateRuleId ?? clientDuplicateRuleId;
  const [initialDraftFingerprint] = useState(() =>
    JSON.stringify(
      buildConditionalPlanCreatePayload(
        conditionQuery,
        thenTag,
        objectSelection,
      ),
    ),
  );
  const isDirty =
    JSON.stringify(
      buildConditionalPlanCreatePayload(
        conditionQuery,
        thenTag,
        objectSelection,
      ),
    ) !==
    initialDraftFingerprint;
  const loadError =
    isEditMode && !editRule
      ? "Rule data is unavailable. Please open edit from the Enhanced Tagging Rule list."
      : null;

  const pageTitle = isEditMode
    ? "Edit Enhanced Tagging Rule"
    : "Add Enhanced Tagging Rule";

  const stepIndex = STEP_ORDER.indexOf(activeStep);
  const isFirstStep = stepIndex <= 0;
  const isLastStep = stepIndex >= STEP_ORDER.length - 1;

  const objectRequiresSubType =
    Boolean(objectSelection.objectType) &&
    getObjectSubTypeOptions(objectSelection.objectType).length > 0;

  const objectStepComplete = isObjectSelectionComplete(
    objectSelection,
    objectRequiresSubType,
  );

  const { conceptOptions } = useConfigurableConcepts(
    objectSelection.objectType,
    objectSelection.objectSubType,
  );
  const fieldLabelByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of conceptOptions) {
      const code = item.code.trim();
      const name = (item.name || item.display_name || "").trim();
      if (code && name) map.set(code, name);
    }
    return map;
  }, [conceptOptions]);

  const conditionStepComplete =
    validateEnhancedTaggingConditionQuery(conditionQuery).valid;

  useEffect(() => {
    let cancelled = false;

    if (
      !objectStepComplete ||
      !conditionStepComplete ||
      !isThenTagComplete(thenTag)
    ) {
      setClientDuplicateRuleId(null);
      return () => {
        cancelled = true;
      };
    }

    const timer = window.setTimeout(() => {
      void findDuplicateConditionalPlan({
        objectType: objectSelection.objectType,
        objectSubType: objectSelection.objectSubType,
        conditionQuery,
        thenTag,
        excludeRuleId: isEditMode ? ruleId : undefined,
      })
        .then((match) => {
          if (!cancelled) setClientDuplicateRuleId(match?.id ?? null);
        })
        .catch(() => {
          if (!cancelled) setClientDuplicateRuleId(null);
        });
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    conditionQuery,
    conditionStepComplete,
    isEditMode,
    objectSelection.objectSubType,
    objectSelection.objectType,
    objectStepComplete,
    ruleId,
    thenTag,
  ]);

  const disabledSteps = useMemo(
    () =>
      objectStepComplete
        ? ([] as const)
        : (["if", "then"] as const satisfies readonly EnhancedTaggingRuleWizardStep[]),
    [objectStepComplete],
  );

  const handleStepChange = useCallback(
    (step: EnhancedTaggingRuleWizardStep) => {
      if (submitting) return;
      if (
        (step === "if" || step === "then") &&
        !isObjectSelectionComplete(objectSelection, objectRequiresSubType)
      ) {
        setShowObjectValidation(true);
        setActiveStep("object");
        showMessage("Please select object type and sub-type.", "warning");
        return;
      }
      setActiveStep(step);
    },
    [objectRequiresSubType, objectSelection, showMessage, submitting],
  );

  const leavePage = useCallback(() => {
    navigate(buildAiPlanPath("enhancedAssessmentPlan"));
  }, [navigate]);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    leavePage();
  }, [isDirty, leavePage]);

  const handleConfirmDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    leavePage();
  }, [leavePage]);

  const handleBack = useCallback(() => {
    if (submitting) return;
    if (isFirstStep) {
      handleClose();
      return;
    }
    setActiveStep(STEP_ORDER[stepIndex - 1]);
  }, [handleClose, isFirstStep, stepIndex, submitting]);

  const handleConditionQueryChange = useCallback((nextQuery: RuleGroupType) => {
    setConditionQuery(nextQuery);
    setClientDuplicateRuleId(null);
    setSubmitDuplicateRuleId(null);
    // Keep sidebar/panel errors until conditions become valid. QueryBuilder can
    // emit onQueryChange on mount when Submit redirects to this step.
    if (validateEnhancedTaggingConditionQuery(nextQuery).valid) {
      setShowConditionValidation(false);
    }
  }, []);

  const handleObjectSelectionChange = useCallback(
    (next: EnhancedTaggingRuleObjectSelection) => {
      const scopeChanged =
        next.objectType !== objectSelection.objectType ||
        next.objectSubType !== objectSelection.objectSubType;
      if (!scopeChanged) return;

      if (hasEnhancedTaggingConditionData(conditionQuery)) {
        setPendingObjectSelection(next);
        setChangeScopeConfirmOpen(true);
        return;
      }

      setObjectSelection(next);
      setShowObjectValidation(false);
    },
    [conditionQuery, objectSelection.objectSubType, objectSelection.objectType],
  );

  const handleCancelChangeScope = useCallback(() => {
    setChangeScopeConfirmOpen(false);
    setPendingObjectSelection(null);
  }, []);

  const handleConfirmChangeScope = useCallback(() => {
    if (pendingObjectSelection) {
      setObjectSelection(pendingObjectSelection);
      setConditionQuery(createEmptyConditionQuery());
      setShowConditionValidation(false);
      setShowObjectValidation(false);
      setClientDuplicateRuleId(null);
      setSubmitDuplicateRuleId(null);
    }
    setChangeScopeConfirmOpen(false);
    setPendingObjectSelection(null);
  }, [pendingObjectSelection]);

  const handleThenTagChange = useCallback((next: EnhancedTaggingThenTag) => {
    setThenTag(next);
    setShowThenValidation(false);
    setClientDuplicateRuleId(null);
    setSubmitDuplicateRuleId(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;

    if (
      !isObjectSelectionComplete(objectSelection, objectRequiresSubType)
    ) {
      setShowObjectValidation(true);
      setActiveStep("object");
      showMessage("Please select object type and sub-type.", "warning");
      return;
    }

    const conditionValidation =
      validateEnhancedTaggingConditionQuery(conditionQuery);
    if (!conditionValidation.valid) {
      setShowConditionValidation(true);
      setActiveStep("if");
      if (conditionValidation.message) {
        showMessage(conditionValidation.message, "warning");
      }
      return;
    }

    if (!isThenTagComplete(thenTag)) {
      setShowThenValidation(true);
      showMessage("Please select Dimension and Value.", "warning");
      return;
    }

    setShowObjectValidation(false);
    setShowConditionValidation(false);
    setShowThenValidation(false);
    setSubmitting(true);
    try {
      const payload = buildConditionalPlanCreatePayload(
        conditionQuery,
        thenTag,
        objectSelection,
      );
      if (isEditMode) {
        await updateConditionalPlan(ruleId, payload);
        showMessage("Enhanced tagging rule updated successfully.", "success");
      } else {
        await createConditionalPlan(payload);
        showMessage("Enhanced tagging rule created successfully.", "success");
      }
      navigate(buildAiPlanPath("enhancedAssessmentPlan"));
    } catch (error) {
      const duplicate = parseDuplicateConditionalPlanError(error);
      if (duplicate) {
        setSubmitDuplicateRuleId(duplicate.id);
        setActiveStep("then");
        return;
      }
      showMessage(
        error instanceof Error
          ? error.message
          : isEditMode
            ? "Failed to update enhanced tagging rule."
            : "Failed to create enhanced tagging rule.",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    conditionQuery,
    isEditMode,
    navigate,
    objectRequiresSubType,
    objectSelection,
    ruleId,
    showMessage,
    submitting,
    thenTag,
  ]);

  const handleNext = useCallback(() => {
    if (submitting) return;

    if (activeStep === "object") {
      if (
        !isObjectSelectionComplete(objectSelection, objectRequiresSubType)
      ) {
        setShowObjectValidation(true);
        showMessage("Please select object type and sub-type.", "warning");
        return;
      }
      setShowObjectValidation(false);
    }

    if (activeStep === "if") {
      const validation = validateEnhancedTaggingConditionQuery(conditionQuery);
      if (!validation.valid) {
        setShowConditionValidation(true);
        if (validation.message) {
          showMessage(validation.message, "warning");
        }
        return;
      }
      setShowConditionValidation(false);
    }

    if (activeStep === "then") {
      void handleSubmit();
      return;
    }

    setActiveStep(STEP_ORDER[stepIndex + 1]);
  }, [
    activeStep,
    conditionQuery,
    handleSubmit,
    objectRequiresSubType,
    objectSelection,
    showMessage,
    stepIndex,
    submitting,
  ]);

  const stepSubtitles = useMemo(() => {
    const objectTypeName = resolvePlanTypeDisplayName(
      planTypes,
      objectSelection.objectType,
    );
    const objectSubTypeName = resolvePlanSubTypeDisplayName(
      planTypes,
      objectSelection.objectType,
      objectSelection.objectSubType,
    );
    const conditionSummary = formatConditionSidebarSummary(
      conditionQuery,
      fieldLabelByCode,
    );
    return {
      object: objectStepComplete
        ? objectSubTypeName
          ? `Object Type is ${objectTypeName}, Object Sub-Type is ${objectSubTypeName}`
          : `Object Type is ${objectTypeName}`
        : "Please select object type and sub-type",
      if: conditionSummary || "Please add at least one enhanced tag",
      then: isThenTagComplete(thenTag)
        ? `Apply tag: ${thenTag.valueLabel || thenTag.valueCode}`
        : "Please add at least one enhanced tag",
    };
  }, [
    conditionQuery,
    fieldLabelByCode,
    objectSelection,
    objectStepComplete,
    planTypes,
    thenTag,
  ]);

  const invalidSteps = useMemo(() => {
    // Only the condition step shows red validation styling in the sidebar.
    if (
      showConditionValidation &&
      !validateEnhancedTaggingConditionQuery(conditionQuery).valid
    ) {
      return ["if"] as const satisfies readonly EnhancedTaggingRuleWizardStep[];
    }
    return [] as const satisfies readonly EnhancedTaggingRuleWizardStep[];
  }, [conditionQuery, showConditionValidation]);

  const panelMeta = STEP_PANEL_META[activeStep];
  const PanelIcon = panelMeta.Icon;

  const panelBody = useMemo(() => {
    if (activeStep === "object") {
      return (
        <EnhancedTaggingRuleObjectStep
          value={objectSelection}
          onChange={handleObjectSelectionChange}
          showValidation={showObjectValidation}
        />
      );
    }

    if (activeStep === "if") {
      return (
        <EnhancedTaggingRuleConditionBuilder
          query={conditionQuery}
          onQueryChange={handleConditionQueryChange}
          targetType={objectSelection.objectType}
          showValidation={showConditionValidation}
        />
      );
    }

    if (activeStep === "when") {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            bgcolor: "#EEF3FF",
            borderRadius: "8px",
            px: 2,
            py: 1.75,
          }}
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 20, color: "#295EF1", mt: "2px", flexShrink: 0 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#262626",
                lineHeight: 1.6,
                mb: 1.5,
              }}
            >
              This rule is triggered when auto-tagging runs. The system checks
              eligible items against the conditions defined in this rule. If the
              conditions are met, the configured tag will be applied
              automatically.
            </Typography>
            <Typography
              sx={{ fontSize: "14px", color: "#262626", lineHeight: 1.6 }}
            >
              Tag dimensions and values must be configured in the{" "}
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => navigate(buildAiPlanPath("taggingPlan"))}
                sx={{
                  color: "#295EF1",
                  fontSize: "14px",
                  fontWeight: 500,
                  verticalAlign: "baseline",
                  cursor: "pointer",
                }}
              >
                Tagging Plan
              </Link>{" "}
              before they can be selected and used in Enhanced Tagging Rules.
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <>
        {duplicateRuleId ? (
          <DuplicateEnhancedTaggingRuleAlert ruleId={duplicateRuleId} />
        ) : null}
        <EnhancedTaggingRuleThenStep
          value={thenTag}
          onChange={handleThenTagChange}
          targetType={objectSelection.objectType}
          showValidation={showThenValidation}
        />
      </>
    );
  }, [
    activeStep,
    conditionQuery,
    duplicateRuleId,
    handleConditionQueryChange,
    handleObjectSelectionChange,
    handleThenTagChange,
    navigate,
    objectSelection,
    showConditionValidation,
    showObjectValidation,
    showThenValidation,
    thenTag,
  ]);

  if (privilegesLoaded && !canManageAutoTagging) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 1,
          px: 3,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Access Denied
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You do not have permission to manage Enhanced Tagging Rules.
        </Typography>
        <Button
          onClick={() => navigate(buildAiPlanPath("enhancedAssessmentPlan"))}
          sx={{ mt: 1 }}
        >
          Back to Organisational AI Plan
        </Button>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 1,
          px: 3,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Unable to load rule
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {loadError}
        </Typography>
        <Button onClick={handleClose} sx={{ mt: 1 }}>
          Back to Organisational AI Plan
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          py: "16px",
          px: "24px",
          flexShrink: 0,
          borderBottom: "1px solid #F0F0F0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <IconButton
            aria-label="Go back"
            onClick={handleClose}
            disabled={submitting}
            sx={{
              width: 32,
              height: 32,
              p: 1,
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            <Box
              component="img"
              src={arrowLeftIcon}
              alt=""
              sx={{
                width: 16.25,
                height: 16,
                display: "block",
                objectFit: "contain",
              }}
            />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Breadcrumbs
              separator="›"
              sx={{
                mb: "4px",
                "& .MuiBreadcrumbs-separator": { color: "#9ca3af", mx: 1 },
              }}
            >
              <Link
                component="button"
                variant="body1"
                onClick={handleClose}
                sx={{
                  color: primaryColor,
                  textDecoration: "none",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Organisational AI Plan
              </Link>
              <Typography
                sx={{
                  color: "#374151",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                {pageTitle}
              </Typography>
            </Breadcrumbs>
            <Typography sx={{ ...pageSectionTitleSx, mb: "4px" }}>
              {pageTitle}
            </Typography>
            <Typography sx={pageSectionSubtitleSx}>
              {isEditMode
                ? "Update when enhanced tagging should apply after auto-tagging runs."
                : "Define when enhanced tagging should apply after auto-tagging runs."}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            disabled={submitting}
            sx={{ width: 32, height: 32, color: "#8c8c8c" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "stretch",
          gap: 2,
          p: 3,
          overflow: "hidden",
          bgcolor: "#F9FAFB",
        }}
      >
        <EnhancedTaggingRuleFlowSidebar
          activeStep={activeStep}
          onStepChange={handleStepChange}
          stepSubtitles={stepSubtitles}
          disabledSteps={disabledSteps}
          invalidSteps={invalidSteps}
        />

        <Box
          sx={{
            flex: "1 1 50%",
            width: "50%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2.5,
              py: 2,
              borderBottom: "1px solid #F0F0F0",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                bgcolor: panelMeta.iconBg,
                color: panelMeta.iconColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PanelIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography
              sx={{ fontSize: "16px", fontWeight: 600, color: "#262626" }}
            >
              {panelMeta.title}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2.5 }}>
            {panelBody}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              px: 2.5,
              py: 2,
              borderTop: "1px solid #F0F0F0",
              flexShrink: 0,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={submitting}
              sx={{
                textTransform: "none",
                height: 32,
                borderColor: "#D9D9D9",
                color: "#262626",
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={submitting}
              sx={brandContainedButtonSx}
            >
              {isLastStep
                ? submitting
                  ? isEditMode
                    ? "Saving…"
                    : "Submitting…"
                  : isEditMode
                    ? "Save"
                    : "Submit"
                : "Next"}
            </Button>
          </Box>
        </Box>
      </Box>
      <DiscardEnhancedTaggingRuleChangesDialog
        open={discardConfirmOpen}
        onCancel={() => setDiscardConfirmOpen(false)}
        onConfirm={handleConfirmDiscard}
      />
      <ChangeObjectScopeDialog
        open={changeScopeConfirmOpen}
        onCancel={handleCancelChangeScope}
        onConfirm={handleConfirmChangeScope}
      />
    </Box>
  );
}
